// Shared data contract for Valyrio udrir.
// Used by the engine, the import scripts, the Worker API and the app.
// Change it only deliberately: every package depends on these shapes.

export type UiLang = 'fi' | 'en';
export type GlossLang = 'fi' | 'en';

/** Where a row came from. 'corrected' = official data fixed with the users' approval in a review session. */
export type Origin = 'official' | 'ours' | 'corrected';

/** Lifecycle of an official lemma relative to the last wiki sync. */
export type LemmaStatus = 'active' | 'new' | 'changed' | 'removed';

export type WordClass =
  | 'noun'
  | 'proper-noun'
  | 'pronoun'
  | 'adjective'
  | 'verb'
  | 'adverb'
  | 'numeral'
  | 'conjunction'
  | 'postposition'
  | 'preposition'
  | 'interjection'
  | 'particle'
  | 'determiner'
  | 'phrase'
  | 'affix'
  | 'other';

export type Gender = 'lunar' | 'solar' | 'terrestrial' | 'aquatic';

/** High Valyrian has 8 cases (wiki abbreviations). */
export type Case = 'nom' | 'acc' | 'gen' | 'dat' | 'loc' | 'ins' | 'com' | 'voc';
export const CASES: readonly Case[] = ['nom', 'acc', 'gen', 'dat', 'loc', 'ins', 'com', 'voc'];

/** Singular, plural, paucal, collective. Sixth-declension nouns also have a collective plural
 * ('colpl') or paucal plural ('pcpl'); these two are not in NUMBERS (the 8×4 table). */
export type GramNumber = 'sg' | 'pl' | 'pc' | 'col' | 'colpl' | 'pcpl';
export const NUMBERS: readonly GramNumber[] = ['sg', 'pl', 'pc', 'col'];

export type Person = 1 | 2 | 3;
export type Tense = 'present' | 'past' | 'future' | 'perfect' | 'pluperfect' | 'future-perfect' | 'aorist' | 'imperfect' | 'past-habitual';
export type Mood = 'indicative' | 'subjunctive' | 'imperative' | 'conditional' | 'optative';
export type Voice = 'active' | 'passive' | 'mediopassive';
export type Degree = 'positive' | 'equative' | 'comparative' | 'superlative';
export type Polarity = 'affirmative' | 'negative';
export type NonFinite =
  | 'infinitive'
  | 'present-participle'
  | 'perfect-participle'
  | 'gerund'
  | 'supine'
  /** HV participles are tagged {nonFinite:'participle', tense, voice} (present/aorist/future/perfect/past-habitual). */
  | 'participle';

/** Grammatical tags of one inflected form. Only the tags relevant to the word class are set. */
export interface FormTags {
  case?: Case;
  number?: GramNumber;
  gender?: Gender; // adjective agreement
  adjPosition?: 'pre' | 'post'; // HV adjectives inflect differently before/after the noun
  person?: Person;
  tense?: Tense;
  mood?: Mood;
  voice?: Voice;
  polarity?: Polarity;
  degree?: Degree;
  nonFinite?: NonFinite;
  /** The adjective's adverbial form (wiki row "Adverbial"), e.g. kastirī. */
  adverbial?: boolean;
}

export interface Form {
  form: string; // lowercase HV with macrons, e.g. "ābroma"
  tags: FormTags;
  /** Seam: stem + ending === form. Present only when the paradigm's segmentation is confident. */
  stem?: string;
  ending?: string;
  ipa?: string; // with stress mark, e.g. "ˈaː.bro.ma"
  /**
   * 'attested': a variant Peterson's lines use that the template does not produce (e.g. sahās
   * for the template's sās), merged in at runtime from `DictionaryBundle.attested` by
   * `Dictionary.paradigm`. Never stored (D1 allows imported/generated/corrected only).
   */
  origin: 'imported' | 'generated' | 'corrected' | 'attested';
}

/**
 * A form the wiki's dialogue and example lines (or the course's Practice lines) use for a lemma,
 * derived at build time (engine/src/attested.ts, scripts/src/lib/attested.ts). Derived data, not
 * official data: the paradigm itself is never changed by it.
 */
export interface AttestedForm {
  lemmaId: string;
  /** The form as written, lowercase NFC (proper nouns keep their capital). */
  form: string;
  /** formKeys of the paradigm cells it stands for. */
  keys: string[];
  /**
   * 'variant': the form to generate first in these cells: a variant the template already lists
   * (tepas beside tepsa), or one the template contracts away that the regular template gives
   * (sahās for sās). 'spelling': the line links it to another form of the lemma (`target`:
   * [[gōukȳvassis|gōukȳvis]]), so it is only read, never generated.
   */
  kind: 'variant' | 'spelling';
  target?: string;
  /** Where it is used: example / dialogue line ids, or lesson:<id> (at most three). */
  sources: string[];
}

export type ParadigmKind = 'noun' | 'adjective' | 'verb' | 'other';

export interface Paradigm {
  kind: ParadigmKind;
  /** Wiki template that produced it, e.g. "HVn1", "hv-adj-1", "HVv". */
  template: string;
  /** Raw template params exactly as in the wikitext (positional as "1","2",...). */
  params: Record<string, string>;
  /** False for irregular or unconfident paradigms: render full forms, never guess a seam. */
  confident: boolean;
  /**
   * In stored data (D1, the bundle) this holds only *override* forms: imported or corrected
   * forms that generation cannot reproduce. Call the engine's `paradigmFor(lemma)` to get the
   * full table (generated forms merged with overrides). Generated forms are never stored,
   * which keeps D1 well inside the free tier's row-write limits.
   */
  forms: Form[];
  /**
   * Only from `paradigmFor`: template alternatives Peterson ruled out (engine
   * rules/paradigm-corrections.ts, e.g. morghūljis). The analyser still reads them; they are never
   * generated or shown in the table. Never stored.
   */
  ruledOut?: Form[];
  /**
   * The wiki's alternative table ("======Alternative======" under Inflection; build-034, e.g.
   * bōsa: bōsra / bōja). `paradigmFor` adds its differing forms to each cell as second variants
   * (the main form first). `forms` holds only its override forms (as above); `confident` false
   * when they exist. Peterson: sr → j is the newer form, sr the older (pt-g0021).
   */
  alt?: { template: string; params: Record<string, string>; confident?: boolean; forms?: Form[] };
}

export interface Sense {
  n: number; // 1-based sense number as on the wiki
  label?: string; // e.g. "copulative"
  exampleIds: string[];
}

export interface ShowRef {
  show: 'got' | 'hotd' | 'telltale' | 'other';
  season?: number;
  episode?: number;
  speaker?: string;
}

export interface Lemma {
  id: string; // stable slug of the wiki title, e.g. "ābra"; own words: "ours:<slug>"
  hv: string; // display headword, e.g. "Ābra" → stored lowercase "ābra" unless proper noun
  pos: WordClass;
  gender?: Gender;
  declension?: string; // e.g. "1", "2", "3", "4", "5", "6", "irregular"
  verbClass?: string;
  ipa?: string;
  origin: Origin;
  status: LemmaStatus;
  wikiTitle?: string;
  wikiRev?: number;
  senses: Sense[];
  paradigm?: Paradigm;
  etymology?: string; // plain text
  derived: string[]; // lemma ids
  categories: string[];
  audio?: string[]; // Peterson recording file names (on the wiki)
  showRefs: ShowRef[];
  duolingo: boolean; // "Appears on Duolingo" / met on Duolingo
  notes?: string; // usage notes, plain text
  /** Official words not on the wiki: the verified Peterson source (content import). */
  source?: ResearchSource;
  // ---- own word superseded by Peterson's (engine/src/own-word-supersede.ts, 2026-10-09) ----
  /** Own words only: the official lemma the users now learn instead (retired: kept, tagged "korvattu"). */
  supersededBy?: string;
  /** When the users decided it (YYYY-MM-DD). */
  supersededAt?: string;
  // ---- an own loanword or borrowed name (scripts/src/lib/own-words.ts kinds loan / name; 16b-loans-names-calendar.md §1) ----
  /** Own words only: the word it was fitted from and that word's language ("camera", "en"); the app tags it "laina". */
  loan?: { from: string; lang: string };
}

/**
 * 'official' = English from the wiki. Finnish has three states:
 * - 'draft': AI-drafted, nobody has checked it ("AI-luonnos · tarkistamatta");
 * - 'checked': an independent AI check pass confirmed the draft with ≥ 95 % confidence
 *   (the users' decision of 2026-10-05). Usable like reviewed text, but always labelled
 *   "AI-tarkistettu", never as checked by the users;
 * - 'reviewed': a user approved it.
 */
export type GlossStatus = 'official' | 'draft' | 'checked' | 'reviewed';

/** Finnish that no one has checked yet. A missing status on Finnish text counts as a draft. */
export const isFiDraft = (status: GlossStatus | undefined): boolean => status === undefined || status === 'draft';
/** Finnish confirmed by the AI check pass, not (yet) approved by a user. */
export const isFiChecked = (status: GlossStatus | undefined): boolean => status === 'checked';

export interface Gloss {
  id: string; // `${lemmaId}:${lang}:${sense}`
  lemmaId: string;
  lang: GlossLang;
  sense: number;
  text: string;
  /** English from the wiki = 'official'. Finnish: 'draft' → 'checked' (AI check pass) → 'reviewed' (a user approved it). */
  status: GlossStatus;
  reviewedBy?: string;
  updatedAt?: string;
}

export interface Example {
  id: string;
  hv: string; // may contain [[links]] stripped; plain text
  en: string;
  fi?: string;
  fiStatus?: GlossStatus;
  speaker?: string;
  show?: ShowRef['show'];
  season?: number;
  episode?: number;
  audio?: string;
  lemmaIds: string[];
  /** Word tokens of `hv` mapped to lemma ids where the wiki linked them. */
  tokens?: { text: string; lemmaId?: string; /** wiki link target, may be an inflected-form page */ link?: string }[];
  /** Not from the wiki: a verified Peterson source (content import; absent on wiki examples). */
  source?: ResearchSource;
  /** A quoted show line's documented errors that fall in this example (bundle only; see DialogueLine.errata). */
  errata?: LineErratum[];
  /** Our notes on a quoted show line that fall in this example (bundle only; see DialogueLine.ourNotes). */
  ourNotes?: OurLineNote[];
}

// --- content import (verified Peterson sources, community examples; scripts/src/lib/research-import.ts) ---
/**
 * Where an official example or word that is not on the wiki comes from: a Peterson source that
 * passed both research checks (data/research/verification). Always shown with the example/entry.
 */
export interface ResearchSource {
  /** 'peterson': his own post, answer, talk or notes; 'duolingo': the Duolingo course he wrote. */
  kind: 'peterson' | 'duolingo';
  url: string;
  /** e.g. "David J. Peterson (Tumblr: dedalvs)". */
  author: string;
  /** ISO date or year as given by the source. */
  date?: string;
  /** Whose English: 'source' = Peterson's / the course's own; 'research' = pieced together from his comments. */
  enBy?: 'source' | 'research';
  /** corrected: his own corrected form of an aired or posted line; hedged: he hedged it; romanised: given
   * in IPA and romanised by the researchers; endorsed: an asker's sentence he confirmed. */
  flags?: ('corrected' | 'hedged' | 'romanised' | 'endorsed')[];
  /** Research row ids (data/research/candidates.jsonl). */
  refs: string[];
}

/**
 * A community example (the Juelos list on the wiki's user pages, provenance 'community'): kept OUT of
 * the dictionary bundle, D1 and the translator's evidence; shown only under its own label
 * ("Yhteisön esimerkit") and usable as practice material. Never Peterson's.
 */
export interface CommunityExample {
  id: string; // "community:<research id>"
  hv: string;
  en: string;
  provenance: 'community';
  author: string;
  url: string;
  lemmaIds: string[];
  tokens: { text: string; lemmaId?: string }[];
  /** AI-drafted Finnish (data/curated/fi-examples.json under the same id); draft / checked / reviewed as on any example. */
  fi?: string;
  fiStatus?: GlossStatus;
  /** Where it comes from when it is not the Juelos list (absent = 'juelos'). */
  collection?: CommunityCollection;
  /** Game lines ('telltale'): the episode and the speaker the transcription gives. */
  episode?: number;
  speaker?: string;
}
/**
 * The community collections: 'juelos' (the Juelos example list on the LI wiki) and 'telltale' (a fan
 * transcription of the Telltale Game of Thrones game's High Valyrian lines; Peterson says he did the
 * game's Valyrian, but the spelling is not verifiably his). Both are community, never Peterson's.
 */
export type CommunityCollection = 'juelos' | 'telltale';
// --- end content import ---

export interface DialogueLine {
  id: string;
  show: ShowRef['show'];
  season: number;
  episode: number;
  scene: number;
  order: number;
  speaker?: string;
  addressee?: string;
  hv: string;
  en: string; // official translation
  fi?: string;
  fiStatus?: GlossStatus;
  /** Peterson recordings of the line (bundle only; not stored in D1). */
  audio?: string[];
  /**
   * The wiki's footnote on the line's High Valyrian, in English (bundle only): mostly Peterson's
   * own mistakes ("This is a typo by Peterson, and should properly be Ērintȳ."). `hv` stays as
   * he wrote it; the app shows the note in small print on the line.
   */
  note?: string;
  /**
   * Documented errors in the line (bundle only; data/curated/show-notes/peterson.json, carried by
   * build:data): what Peterson himself said, else the wiki's footnote. `hv` stays as aired; the app
   * shows a short note under the line wherever it appears.
   */
  errata?: LineErratum[];
  /**
   * Our own notes on the line (bundle only; data/curated/line-notes.json, carried by build:data):
   * origin 'ours', never Peterson's or the wiki's word; shown with the "meidän" tag.
   */
  ourNotes?: OurLineNote[];
  /**
   * The line is writing seen on screen, not speech (bundle only; scripts/src/lib/on-screen.ts):
   * Talisa's letter. No recording of it exists; `sources` are where its text is published.
   */
  written?: { what: 'letter'; sources: WrittenSource[] };
}

/** Where an on-screen text is published (DialogueLine.written): the wiki page, Peterson's post. */
export interface WrittenSource {
  by: 'peterson' | 'wiki';
  url: string;
  /** YYYY-MM-DD (Peterson's post). */
  date?: string;
}

/**
 * Our note on a show line (DialogueLine.ourNotes / Example.ourNotes): what we could not explain
 * from the sources, agreed with the users in a review session. Never a claim about the language.
 */
export interface OurLineNote {
  /** The word of the line it is about (as written). */
  word: string;
  fi: string;
  en: string;
  /** YYYY-MM-DD the users agreed it. */
  date: string;
}

/** One documented error in a show line (DialogueLine.errata). */
export interface LineErratum {
  /** The word as written in the line; `wordEnd` the last word of a span ("pirte … prūmie"). */
  word: string;
  wordEnd?: string;
  /** Which occurrence of `word` in the line (1 = first; absent = 1). */
  occurrence?: number;
  /** The aired form and the correct one. */
  heard: string;
  expected: string;
  /** Who documented it: Peterson himself (his source wins), else only the wiki's footnote. */
  by: 'peterson' | 'wiki';
  url: string;
  /** YYYY-MM-DD, YYYY-MM or YYYY (Peterson's sources). */
  date?: string;
  /** The wiki's own footnote on this error, verbatim, when it is part of `note` (shown once, not twice). */
  wikiNote?: string;
  /** The line is on-screen writing (DialogueLine.written): the error was seen, not heard. */
  seen?: true;
}

/** The offline dictionary bundle the app caches in IndexedDB and the engine consumes. */
export interface DictionaryBundle {
  version: string; // content hash
  generatedAt: string;
  lemmas: Lemma[];
  glosses: Gloss[];
  examples: Example[];
  dialogue: DialogueLine[];
  /** Attested-form index (derived at build time; absent in older bundles and fixtures). */
  attested?: AttestedForm[];
  /** Translations the users approved (translation memory, origin 'ours'; absent when there are none). */
  approved?: ApprovedTranslation[];
  /**
   * Content hash of the form index the data build ships with this bundle (version.ts
   * formIndexStamp): a new bundle whose forms did not change keeps the index the phones have.
   * Absent in older bundles and fixtures (the stamp then follows `version`).
   */
  formIndexHash?: string;
}

/**
 * A translation Axel and Roosa approved (translator phase 3 items 9, 11, 20): a translator flag
 * they resolved with a corrected translation (data/curated/translation-memory.json, written by
 * review:push), or an approved sentence of the sentence stack (data/curated/sentence-stack/).
 * Origin 'ours': never official High Valyrian, always shown with the "meidän / ours" tag. The
 * translator screen reuses it whole, in pieces and as an analogy frame; never the score.
 */
export interface ApprovedTranslation {
  id: string;
  /** At least two of the three sides; the HV side is always present. */
  hv: string;
  en?: string;
  fi?: string;
  source: 'flag' | 'stack';
  /** The flag id or stack sentence id it came from. */
  ref?: string;
  /** User id who approved it, and when (ISO). */
  approvedBy?: string;
  approvedAt?: string;
  /**
   * An AI-checked stack sentence (StackSentence status 'checked'): usable like an approved one but
   * no user has approved it; the app labels it "AI-tarkistettu", never as the users' approval.
   */
  aiChecked?: boolean;
  /** An AI-checked sentence whose HV an AI fixed ("AI-korjattu · AI-tarkistettu", StackSentence.aiFix). */
  aiFixed?: boolean;
  /** An AI-checked sentence whose Finnish a user decided (user id; StackSentence.fiBy). */
  fiApprovedBy?: string;
}

// ---------- Course ----------

export interface LessonMeta {
  id: string; // e.g. "10-genitive" (NN-slug: NN = order)
  order: number; // 1-based; opens on the Saturday of week `order` once both passed order − 1 (course.ts)
  titleFi: string;
  titleEn: string;
  duolingoUnit?: string;
  lemmaIds: string[]; // words this lesson releases into the new-word queue, in order
  exampleIds: string[];
}

// ---------- Lesson hub (docs/COURSE.md "Lesson hub") ----------
// A lesson is the week's hub and its meta the one source of truth for that week: the Kielioppi
// sections it teaches, the form drills it opens, the show lines to listen to, the pronunciation
// focus, the phrases, the week's letter and the role-play scene. Everything else is derived.

/** The sound or prosody a week's Puhu step drills (labels: app i18n course.focus). */
export type PronunciationFocus =
  | 'stress'
  | 'long-vowels'
  | 'y'
  | 'r'
  | 'z'
  | 'j'
  | 'q'
  | 'gh'
  | 'palatals'
  | 'diphthongs'
  | 'lengthening'
  | 'command-stress'
  | 'rhythm';
export const PRONUNCIATION_FOCI: readonly PronunciationFocus[] = [
  'stress',
  'long-vowels',
  'y',
  'r',
  'z',
  'j',
  'q',
  'gh',
  'palatals',
  'diphthongs',
  'lengthening',
  'command-stress',
  'rhythm',
];

/** Form drills a lesson opens: a drilled form needs the lesson of each of its tags passed. */
export interface LessonDrills {
  cases?: Case[];
  numbers?: GramNumber[];
  tense?: Tense[];
  mood?: Mood[];
  voice?: Voice[];
  degree?: Degree[];
  nonFinite?: NonFinite[];
}

/**
 * The Halloween script's work a lesson brings (Axel 2026-10-09; engine/src/pedagogy/halloween-script.ts):
 * 'ideas' the sentence inbox opens, 'esitys' the script is rehearsed as Esitys cards, 'stage' Lavalla.
 * The first lesson naming each opens it for both (halloweenGates).
 */
export type HalloweenWork = 'ideas' | 'esitys' | 'stage' | 'rehearsal';
/** 'rehearsal' (the course-wiring audit of 2026-10-09): the dress rehearsal from this lesson on holds every approved line whose words are released. */
export const HALLOWEEN_WORK: readonly HalloweenWork[] = ['ideas', 'esitys', 'stage', 'rehearsal'];

/**
 * Omat virheet (Axel 2026-10-09; engine/src/pedagogy/own-errors.ts): where a person's own mistakes are
 * collected from. 'card' typed answers on cards, 'exercise' fill and case answers, 'dictation' words
 * written wrong in a dictation, 'letter' the partner's corrections of one's letters. The first lesson
 * naming each in its meta (`ownErrors`) starts collecting it (ownErrorGates).
 */
export type OwnErrorSource = 'card' | 'exercise' | 'dictation' | 'letter';
export const OWN_ERROR_SOURCES: readonly OwnErrorSource[] = ['card', 'exercise', 'dictation', 'letter'];

export interface LessonHub {
  /** Every Duolingo unit the lesson covers (the "Duolingo: X" label); `duolingoUnit` is the first. */
  duolingoUnits?: string[];
  /** Kielioppi topic ids this lesson teaches first ("opit tämän tunnilla N"). */
  grammar?: string[];
  /** Kielioppi topics it comes back to. */
  grammarAlso?: string[];
  /** The week's show lines (dialogue ids; the actors' clips attach as `show:<id>`). */
  showLines?: string[];
  /** The week's Puhu step: a focus, dictionary headwords and dialogue lines with Peterson's audio. */
  pronunciation?: { focus: PronunciationFocus; words?: string[]; lines?: string[] };
  /** Phrases to learn whole (lemma ids of phrase and interjection entries). */
  phrases?: string[];
  /**
   * The week's letter to each other (one writing task: notebook and Kirjeet); `model` a show line
   * to read as the model letter (a written line: "got-s3e07-letter", Talisa's letter).
   */
  letter?: { fi: string; en: string; model?: string };
  /** The week's role-play scene ("got-s3e06-1", app/src/together/scenes.ts sceneKey). */
  scene?: string;
  /** Form drills this lesson opens. */
  drills?: LessonDrills;
  /** The Halloween script's work of the week (HalloweenWork). */
  halloween?: HalloweenWork[];
  /** Omat virheet: the mistakes this lesson starts collecting as error cards (OwnErrorSource). */
  ownErrors?: OwnErrorSource[];
  /** Sitkeät sanat (pedagogy/memory-images.ts): the first lesson saying so opens the memory images for both. */
  memoryImages?: true;
  /** Halloween-mittari (pedagogy/halloween-meter.ts): the week hub shows the meter's sentence this week. */
  halloweenMeter?: true;
  /** Lukupino opens with this lesson (engine pedagogy/reading.ts readingFromLesson; the first such lesson). */
  reading?: true;
  /** Per-person stress focus ("Harjoitellaan painoa") is used from this lesson (pedagogy/stress-focus.ts stressFocusFromLesson). */
  stressFocus?: true;
  /** Ääntämisvalmentaja is offered from this lesson to a person who turned it on (pedagogy/coach.ts coachFromLesson; the first such lesson). */
  coach?: true;
}

export interface PaceConfig {
  /** ISO date (a Saturday, course.ts LESSON_WEEKDAY) on which lesson 1 opens; early opens move the timetable, never this. */
  startDate: string;
  /**
   * Optional earlier opening of lesson 1 only (ISO date before startDate): the users try lesson 1
   * early; every later lesson and the word pace keep the startDate timetable (Axel, 2026-10-06).
   */
  firstLessonDate?: string;
  newPerDayFirstMonth: number; // 5
  newPerDayAfter: number; // 9: what spreads every course word to the last lesson (scripts/src/lib/course-plan.ts)
  firstMonthDays: number; // 30
  lessonsPerWeek: number; // 1
  /** Pause new words when either user has more than this many overdue reviews. */
  pauseIfOverdueAbove: number;
  timezone: string; // "Europe/Helsinki"
  /** The goal the course is paced to (ISO date): Halloween 2027, the users' decision of 2026-10-05. */
  goalDate?: string;
  /**
   * Lessons opened early by a joint vote (Axel, 2026-10-07; docs/COURSE.md "Opening early"). Never
   * admin-set: the server attaches its early_open rows (api/src/frontier.ts loadPace) and the pace
   * form never sends them back. Each moves its lesson and every later one earlier by the same
   * number of days, a week apart from the new date (engine/src/course.ts earlyOpensOf).
   */
  earlyOpens?: EarlyOpen[];
  /**
   * The rising word pace after the first month (Axel, 2026-10-14; docs/COURSE.md "Pacing",
   * engine/src/course.ts paceSteps). Absent = the old flat pace: newPerDayAfter every day after the
   * first month (old D1 rows and old clients keep working).
   */
  staircase?: PaceStaircase;
  /**
   * The steps the staircase has actually reached (server state: api/src/frontier.ts attaches the
   * frontier's record, like earlyOpens; never admin-set). Absent = the calendar's steps (the build,
   * an estimate); present = the rate never runs ahead of the steps reached, so a held step holds the
   * rate and the release target with it (no catch-up flood after a hold).
   */
  rises?: PaceRise[];
}

/**
 * New words a day after the first month: `start` on its first day, `step` more at each of `steps`
 * even intervals up to `until` (the last words' day), then the top rate (Axel, 2026-10-14). The build
 * computes `steps` and `until` from the number of course words (scripts/src/lib/course-plan.ts ratePlan).
 */
export interface PaceStaircase {
  /** New words a day on the first stair (6). */
  start: number;
  /** Words a day added at each rise (1). */
  step: number;
  /** Stairs from `start` to the top (7 with 3025 course words: 6 → 12). */
  steps: number;
  /** The staircase's last day on the Saturday timetable (ISO date, the last words' day: 2027-09-24). */
  until: string;
  /**
   * A rise needs both of you to have kept your recall: the share of review-state card reviews
   * answered right over the last two weeks (pedagogy/logs.ts reviewSample) at least this (0–1).
   */
  minRecall: number;
  /**
   * An initial hold (Axel 2026-10-25: "new words are too many", the first three months are for grammar): on every
   * course day up to and including `until` at most `perDay` new words a day in all (daily allowance, lesson words,
   * own words alike; follow-up cards of a word already started never count). The stairs begin the day after it
   * (or at the first month's end, whichever is later) and climb evenly from there to the staircase's `until`.
   * Absent = no hold (old rows and clients).
   */
  hold?: PaceHold;
}

/** The staircase's initial hold (PaceStaircase.hold). */
export interface PaceHold {
  /** New words a day at most while it lasts (2). */
  perDay: number;
  /** Its last day (ISO date on the Saturday timetable: 2027-01-10). */
  until: string;
}

/** A stair the rate reached (`step` ≥ 1: the first stair is 0) on local date `date`. */
export interface PaceRise {
  step: number;
  date: string;
}

/** A lesson opened before its date because both of you said yes (early_open row). */
export interface EarlyOpen {
  /** The lesson's order (the first lesson of its week). */
  lesson: number;
  /** The local date it opened ("YYYY-MM-DD", pace timezone). */
  date: string;
}

/**
 * 'sentence': a translation made into a card (translator phase 3 item 19; api.ts SentenceCard).
 * 'listen': hear a word, recall its meaning (itemId = the lemma id; pedagogy/listen-cards.ts, 2026-10-09).
 */
export type CardType = 'hv-native' | 'native-hv' | 'form-drill' | 'sentence' | 'listen';

export interface CardKey {
  userId: string;
  itemId: string; // lemma id, or `${lemmaId}#${formKey}` for form drills
  type: CardType;
}

// ---------- Review loop ----------

export type FlagKind =
  | 'fi-meaning'
  | 'en-meaning'
  | 'translator'
  | 'missing-word'
  | 'grammar'
  | 'lesson'
  | 'segmentation'
  | 'form'
  | 'example'
  | 'bug'
  | 'idea'
  | 'official-change'
  // a wiki sync found Peterson's own word for an own word's meaning / spelled like an own word
  // (scripts/src/lib/own-word-supersede.ts, 2026-10-09)
  | 'ours-superseded'
  | 'ours-collision'
  // Peterson's word for an open own-word need (no word yet)
  | 'need-answered';

/** open → (Claude) handled → (user) closed; a user may reopen a handled flag. */
export type FlagStatus = 'open' | 'handled' | 'closed';

export interface FlagTarget {
  table: 'lemma' | 'form' | 'gloss' | 'example' | 'dialogue' | 'lesson' | 'translator' | 'proposal' | 'app';
  id: string;
  /** Screen and context the flag was raised from, so a review session sees what the user saw. */
  screen?: string;
  context?: unknown;
}

export interface Flag {
  id: string;
  target: FlagTarget;
  kind: FlagKind;
  note: string;
  suggested?: string;
  createdBy: string;
  createdAt: string;
  status: FlagStatus;
  resolution?: string; // Claude's note
  resolvedAt?: string;
  closedBy?: string;
  closedAt?: string;
  thread: { by: string; at: string; text: string }[];
  /** Kind 'translator' only: the user's marks on the translation (TranslatorAnnotation, below). */
  annotation?: TranslatorAnnotation;
}

export type ProposalStatus = 'proposed' | 'approved' | 'rejected' | 'need';

export interface Proposal {
  id: string;
  hv?: string; // empty for a bare "need"
  pos?: WordClass;
  fi?: string;
  en?: string;
  construction?: string; // "root + suffix", etc.
  candidates?: { hv: string; construction: string; note?: string; pos?: WordClass }[];
  proposedBy: string;
  approvedBy?: string;
  status: ProposalStatus;
  createdAt: string;
  /** Why it was rejected (shown to both). */
  note?: string;
  /**
   * The inflection the proposer approved for the new word (translator phase 4 item 18: a class
   * suggested from its ending, names.ts suggestNounClass). Approving the proposal creates the word
   * with this paradigm, so it inflects; without it the word has no table.
   */
  inflection?: ProposalInflection;
}

export interface ProposalInflection {
  /** Wiki inflection template (paradigm-templates.ts) and its parameters. */
  template: string;
  params: Record<string, string>;
  gender?: Gender;
  declension?: string;
  /** The rule that suggested it (rules/names.ts or the validator's class table). */
  ruleId: string;
  /** Chosen by the borrowing rules (lesson 35). */
  borrowed?: boolean;
}

// ---------- Translator ----------

export type TranslateDirection = 'hv-fi' | 'hv-en' | 'fi-hv' | 'en-hv';

export interface TokenReading {
  lemmaId: string;
  form: string;
  tags: FormTags;
  stem?: string;
  ending?: string;
  gloss: string; // gloss in the target language
  caseLabel?: string; // translated label, e.g. "komitatiivi" / "comitative"
}

export interface TokenAnalysis {
  text: string; // surface token
  readings: TokenReading[]; // all possible readings, best first
  chosen?: number; // index into readings
  unknown: boolean;
}

export interface TranslationResult {
  direction: TranslateDirection;
  input: string;
  output: string;
  alternatives: string[];
  confidence: number; // 0..1
  tokens: TokenAnalysis[];
  notes: string[]; // e.g. "word order changed: verb moved"
  /**
   * The same notes as data, in the same order as `notes` (`text` is the English sentence of
   * `notes`). `id` names the rule that wrote the note and stays stable, so a screen can say it in
   * its own language; `args` are the words the note quotes.
   */
  noteItems?: TranslationNote[];
  /**
   * EN/FI → HV only: every High Valyrian word of `output` as generated (lemma and grammatical
   * tags), in output order. Words kept in «guillemets» (no HV equivalent) are not HV words and
   * have none.
   */
  hvTokens?: HvOutputToken[];
  /** Why the confidence is what it is, most important first (engine/src/confidence.ts). */
  confidenceReasons?: ConfidenceReason[];
  /**
   * The input as the translator read it, when that differs from `input` (spoken Finnish, clitics,
   * casual English, "you two", HV macrons and typos; engine/src/input-fixes.ts). `input` stays the
   * text as typed. For HV → X the analysed HV words are those of `readAs`.
   */
  readAs?: string;
  /** Every change behind `readAs`, in text order (also said in `noteItems`). */
  inputFixes?: InputFix[];
  /**
   * EN/FI → HV only: the self-check of `output` (engine/src/self-check.ts): every word a paradigm
   * cell or attested form, agreement, governed cases, and whether the HV reads back with the roles
   * it was generated with.
   */
  selfCheck?: HvSelfCheck;
  /**
   * The words of `input` as the marking screen counts them (annotation.ts markTokens), set by
   * alignSource() (not by translate()); with it, `hvTokens[].src` points into this list.
   */
  sourceTokens?: TokenRef[];
}

// ---------- Self-check of an HV text (engine/src/self-check.ts) ----------

/** The four checks, in this order: forms, agreement, government (cases the verbs and adpositions require), roles (round trip). */
export type HvCheckId = 'forms' | 'agreement' | 'government' | 'roles';

/** One problem a check found. `id` names the rule (rules/self-check.ts) and stays stable. */
export interface HvCheckProblem {
  id: string;
  /** 'fail' fails the check; 'warn' is a caveat (an ambiguous reading) that does not. */
  level: 'fail' | 'warn';
  /** Token column of the HV text (as hvTokenColumns counts them). */
  at?: number;
  args?: Record<string, string | number>;
  /** The English sentence (logs, the review files). */
  text: string;
}

export interface HvCheckResult {
  id: HvCheckId;
  pass: boolean;
  /** Nothing to check (e.g. roles of a text without generated words). A skipped check passes. */
  skipped?: boolean;
  problems: HvCheckProblem[];
}

export interface HvSelfCheck {
  /** Every check passed. */
  pass: boolean;
  checks: HvCheckResult[];
  /**
   * translate() replaced the best candidate, whose roles did not read back, with a clearer
   * alternative; the replaced candidate is the first of `alternatives`.
   */
  swapped?: boolean;
}

// ---------- Sentence stack (data/curated/sentence-stack; phase 3 item 20) ----------

/**
 * One stack sentence as the approval screen reads it (/data/sentence-stack.json, written by
 * `npm run stack:suggest`): the users' everyday EN/FI sentence (AI-drafted, unreviewed) with the
 * engine's HV suggestion and its self-check. Never official, never reviewed until a user approves it.
 */
export interface StackSuggestion {
  id: string;
  theme: string;
  topic: string;
  en: string;
  fi: string;
  /** The suggested HV ('' when the engine has none). */
  hv: string;
  /** Other HV suggestions (the other input language's translation, alternatives). */
  alt?: string[];
  /** Calibrated translator confidence of `hv` (0..1). */
  confidence: number;
  /** "Sääntöjen mukaan oikein": every check passed and the EN and FI inputs agree on the HV (or one is a strict improvement). */
  rulesOk: boolean;
  /** Ids of the checks `hv` failed (HvCheckId). */
  failed: string[];
  /** Source words without an HV word. */
  unknown: string[];
  /** Easiest first: the order the approval screen and /review go through the stack. */
  rank: number;
  /** The AI judges rejected this HV (stack:ai-apply, ai-rejected.jsonl): the approval screen puts it last. */
  aiRejected?: boolean;
}

/** File /data/sentence-stack.json (and data/build/sentence-stack.json). */
export interface StackSuggestionFile {
  /** Engine version the suggestions were made with (stack:suggest). */
  engine: string;
  items: StackSuggestion[];
}

/**
 * A stack sentence the users handled (D1 table stack_sentence, GET /api/stack). Approved ones are
 * origin 'ours' translations: the translation memory shows them as "Meidän käännös", never as
 * official. `fi` and `hv` are what the approver saw and accepted (possibly fixed).
 * 'checked' (Axel's decision of 2026-10-07): at least two of three independent AI judges found the
 * HV and the Finnish to match the English (`npm run stack:ai-apply`, the only writer). Usable like
 * an approved one ("Meidän käännös · AI-tarkistettu"), never presented as approved by the users;
 * they can still approve, fix or skip it.
 */
export interface StackSentence {
  id: string;
  en: string;
  fi: string;
  hv: string;
  status: 'approved' | 'skipped' | 'checked';
  origin: 'ours';
  /** The approver changed the suggested HV (or approved without a suggestion). */
  edited: boolean;
  /** Engine version of the suggestion that was shown. */
  engine?: string;
  approvedBy?: string;
  updatedBy: string;
  updatedAt: string;
  /** The AI check run that set status 'checked' (kept after a user decides), and when. */
  checkedBy?: string;
  checkedAt?: string;
  /**
   * AI-fixed (stack:ai-apply, Axel's decision of 2026-10-07): the judges rejected the suggestion
   * `originalHv`, an AI fixer wrote `hv` from dictionary forms and three new judges checked the fix
   * ("AI-korjattu · AI-tarkistettu"; never the users' fix or approval).
   */
  aiFix?: { originalHv: string };
  /** A user decided the Finnish of this AI-checked sentence (user id): "Suomennoksen hyväksynyt: …". */
  fiBy?: string;
  /**
   * The Finnish of this AI-checked sentence was corrected in the repo after the AI check
   * (stack:fi-sync, check_votes.fiFix): the judges' Finnish votes were on the earlier text.
   */
  fiFixed?: boolean;
}

/**
 * One input normalisation (TranslationResult.inputFixes). kind: 'fiSpoken' (spoken → standard
 * Finnish), 'fiClitic' (an enclitic particle said as a word or dropped), 'enCasual' (gonna, u,
 * dont …), 'enTypo' and 'hvTypo' (one-letter typos, a guess), 'hvLetters' (macrons / ñ added),
 * 'you' ("you two" → you, plural).
 */
export interface InputFix {
  kind:
    | 'fiSpoken'
    | 'fiClitic'
    | 'enCasual'
    | 'enTypo'
    | 'hvLetters'
    | 'hvTypo'
    | 'you'
    // phase 5 items 1, 5, 9: British spelling, phrasal/light verbs, our paraphrases (phrase-rewrites.ts)
    | 'enSpelling'
    | 'phrasal'
    | 'paraphrase';
  /** As typed. */
  from: string;
  /** As read ('' when a particle was dropped). */
  to: string;
  /** The rule's id (rules/fi-spoken.ts, rules/en-casual.ts, rules/hv-spelling.ts). */
  rule: string;
  /** Typos: other real words at the same distance, best first. */
  alternatives?: string[];
}

/** A translator note as data (TranslationResult.noteItems). */
export interface TranslationNote {
  id: string;
  args?: Record<string, string | number>;
  /** The English sentence (as in TranslationResult.notes). */
  text: string;
}

/** One generated HV word of an EN/FI → HV translation (TranslationResult.hvTokens). */
export interface HvOutputToken {
  /** The word as written in the output (capitalised where the output is). */
  form: string;
  lemmaId: string;
  tags: FormTags;
  /**
   * Index of the word among the output's tokens, counted as the HV analyser tokenises the output
   * sentence by sentence (the same columns `analyseHvSentence(output)` returns).
   */
  at: number;
  /** The source word it was generated from (index into TranslationResult.sourceTokens; set by alignSource()). */
  src?: number;
}

/**
 * A reason for the confidence level, as data. Ids: 'allKnown', 'unknown' (args.n = words),
 * 'noVerb', 'closest', 'endingOnly', 'fallbackGloss', 'unsureFi', 'approx', 'names' (args.n),
 * 'draft', 'order', 'typo' (a one-letter typo was corrected).
 */
export interface ConfidenceReason {
  id: string;
  args?: Record<string, string | number>;
}

export interface TranslatorCase {
  id: string;
  input: string;
  direction: TranslateDirection;
  expected: string[]; // any of these is a pass
  source: 'dialogue' | 'example' | 'flag' | 'manual';
  lastOutput?: string;
  pass?: boolean;
}

// ---------- Translator marks ("Merkitse virheet"; docs/TRANSLATOR-MARKS.md, phase 5 items 11–12) ----------

/**
 * A word of the translator's input ('source') or output ('output'): its index among that side's
 * words (engine/src/annotation.ts markTokens), the word as shown and its character offset.
 */
export interface TokenRef {
  side: 'source' | 'output';
  i: number;
  text: string;
  start: number;
}

export type MarkPos = 'verb' | 'noun' | 'adjective' | 'name';
export type MarkRole = 'subject' | 'object' | 'recipient' | 'addressee' | 'possessor' | 'place';

/** One mark a user put on a translation (engine/src/annotation.ts validates, turns into hints and checks them). */
export type TranslatorMark =
  | { type: 'pos'; at: TokenRef; pos: MarkPos }
  | { type: 'meaning'; at: TokenRef; lemmaId?: string; text?: string; lang?: 'hv' | 'fi' | 'en' }
  | { type: 'role'; at: TokenRef; role: MarkRole }
  | { type: 'form'; at: TokenRef; tense?: 'present' | 'past' | 'future'; number?: 'sg' | 'pl'; mood?: 'command' }
  | { type: 'sentence'; question?: boolean; negative?: boolean; you?: 'sg' | 'pl'; tone?: 'intimate' | 'formal' }
  | { type: 'missing'; at: TokenRef }
  | { type: 'extra'; at: TokenRef }
  | { type: 'insert'; after: TokenRef | null; text: string }
  | { type: 'expected-word'; at: TokenRef; text: string };

export type TranslatorMarkType = TranslatorMark['type'];

/** What a review session did with one mark (written by review:push from a resolution's outcomes). */
export interface MarkOutcome {
  /** Index into TranslatorAnnotation.marks. */
  mark: number;
  result: 'fixed' | 'proposal' | 'memory' | 'open';
  note: string;
}

/**
 * The marks on one translator flag (D1 flag.annotation, JSON; kind 'translator' only). `input`,
 * `direction` and `output` are the translation as it was marked (before the marks were applied);
 * `build` is the dictionary version it came from.
 */
export interface TranslatorAnnotation {
  v: 1;
  input: string;
  direction: TranslateDirection;
  output: string;
  build: string;
  /** At most 30 marks; the whole annotation at most 8 KB (validateAnnotation). */
  marks: TranslatorMark[];
  /** The translation the user expected (typed in the flag sheet). */
  expected?: string;
  /** The output with the marks applied, as it was on screen when sent. */
  pinnedOutput?: string;
  /** "Nyt oikein" / "Vieläkin väärin" under the re-translated output. */
  verdict?: 'right' | 'still-wrong';
  /** Per-mark outcomes, written by the review session (review:push). */
  outcomes?: MarkOutcome[];
}
